import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import type {
  ColDef,
  FirstDataRenderedEvent,
  GridReadyEvent,
  GridSizeChangedEvent,
} from "ag-grid-community";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  searchPartsDictionary,
  type PartsSearchDictionaryResult,
} from "../../apiServices/partsSearchApi";
import { useUserStore } from "../../store/userStore";
import "./PartsSearchPage.css";

ModuleRegistry.registerModules([AllCommunityModule]);

const PartsSearchPage: React.FC = () => {
  const user = useUserStore((s) => s.user);
  const role = String(user?.role || "").trim().toUpperCase();
  const vendorId = Number(user?.vendorId || 0);

  const basePath = process.env.REACT_APP_BASE_NAME || "";
  const oemFromBase = basePath.split("/")[1] || "";
  const oemFromUser = user?.oemId ? `Oem${user.oemId}` : "";
  const oem = oemFromBase || oemFromUser || "Oem1";

  const [keyword, setKeyword] = useState("");
  const [results, setResults] = useState<PartsSearchDictionaryResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const normalizedRoleForApi = useMemo(() => {
    if (!role) return "";
    return role.toLowerCase();
  }, [role]);

  const handleSearch = async () => {
    const q = keyword.trim();
    setHasSearched(true);
    setError(null);

    if (!q) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const data = await searchPartsDictionary(oem, q, {
        role: normalizedRoleForApi,
        vendorId: role.includes("VENDOR") ? vendorId : undefined,
      });
      setResults(data);
    } catch (e: any) {
      setResults([]);
      setError(e?.message || "Failed to search parts.");
    } finally {
      setLoading(false);
    }
  };

  const buildDetailLink = useCallback((row: PartsSearchDictionaryResult) => {
    const query = new URLSearchParams();
    if (row.figId && row.figId > 0) query.set("figId", String(row.figId));

    const parsedItem = Number(row.itemNumber);
    if (Number.isFinite(parsedItem) && parsedItem > 0) {
      query.set("itemNumber", String(parsedItem));
    }

    const qs = query.toString();
    return `/component-detail/${oem}/${row.masterAssemblyId}${qs ? `?${qs}` : ""}`;
  }, [oem]);

  const fitColumns = useCallback((api: { sizeColumnsToFit: () => void } | undefined) => {
    if (!api) return;
    try {
      api.sizeColumnsToFit();
    } catch {
      // ignore
    }
  }, []);

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      fitColumns(params.api);
    },
    [fitColumns]
  );

  const onGridSizeChanged = useCallback(
    (params: GridSizeChangedEvent) => {
      fitColumns(params.api);
    },
    [fitColumns]
  );

  const onFirstDataRendered = useCallback(
    (params: FirstDataRenderedEvent) => {
      fitColumns(params.api);
    },
    [fitColumns]
  );

  const columnDefs: ColDef<PartsSearchDictionaryResult>[] = useMemo(
    () => [
      {
        headerName: "Action",
        field: "masterAssemblyId",
        minWidth: 82,
        maxWidth: 92,
        sortable: false,
        filter: false,
        floatingFilter: false,
        cellRenderer: (params: any) => (
          <Link to={buildDetailLink(params.data)} className="parts-search-link">
            Open
          </Link>
        ),
      },
      { headerName: "Master Part Number", field: "masterPartNumber", minWidth: 130 },
      {
        headerName: "Component Description",
        field: "masterAssemblyDescription",
        minWidth: 170,
        flex: 1.1,
      },
      { headerName: "Ver", field: "currentVersionId", minWidth: 70, maxWidth: 80, filter: "agNumberColumnFilter" },
      {
        headerName: "Figure",
        minWidth: 160,
        valueGetter: (params) =>
          [params.data?.figNumber, params.data?.figTitle].filter(Boolean).join(" - ") || "-",
      },
      { headerName: "Item Number", field: "itemNumber", minWidth: 90 },
      { headerName: "Part Number", field: "partNumber", minWidth: 120 },
      { headerName: "Part Description", field: "partDescription", minWidth: 170, flex: 1.2 },
    ],
    [buildDetailLink]
  );

  const defaultColDef: ColDef = useMemo(
    () => ({
      filter: true,
      floatingFilter: true,
      sortable: true,
      resizable: true,
      minWidth: 70,
      flex: 1,
      cellStyle: { display: "flex", alignItems: "center" },
    }),
    []
  );

  return (
    <div className="parts-search-page">
      <div className="parts-search-card">
        <div className="parts-search-head">
          <h3>Parts Search</h3>
          <p>Search by part number or nomenclature.</p>
        </div>

        <div className="parts-search-controls">
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void handleSearch();
              }
            }}
            placeholder="Enter keyword..."
          />
          <button type="button" onClick={() => void handleSearch()} disabled={loading}>
            {loading ? "Searching..." : "Search"}
          </button>
        </div>
      </div>

      {hasSearched ? (
        <div className="parts-search-table-wrap">
          {error ? <div className="parts-search-message error">{error}</div> : null}
          {!loading && !error && results.length === 0 ? (
            <div className="parts-search-message">No results found.</div>
          ) : null}

          {results.length > 0 ? (
            <>
              <div className="parts-search-pagination__meta">
                Total results: {results.length}
              </div>
              <div className="parts-search-grid ag-theme-alpine">
                <AgGridReact
                  rowData={results}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  onGridReady={onGridReady}
                  onGridSizeChanged={onGridSizeChanged}
                  onFirstDataRendered={onFirstDataRendered}
                  domLayout="autoHeight"
                  headerHeight={42}
                  floatingFiltersHeight={36}
                  rowHeight={44}
                  pagination
                  paginationPageSize={10}
                  paginationPageSizeSelector={[10, 25, 50]}
                  animateRows
                />
              </div>
            </>
          ) : null}
        </div>
      ) : (
        <div className="parts-search-message">Search results will appear here.</div>
      )}
    </div>
  );
};

export default PartsSearchPage;
